// Réglages → Accès privé, et fenêtre « Accès » d'un compte (lien personnel, navigateurs autorisés).
import { useCallback, useEffect, useState } from "react";
import { api, unwrap } from "../api/client";
import type { components } from "../api/schema";
import type { UserView } from "../api/types";
import { formatTime } from "../lib/format";
import { rich, t } from "../lib/i18n";
import { Button, ConfirmDialog, CopyField, FormError, Modal, Panel, Pill, useToast } from "./ui";

type Device = components["schemas"]["DeviceView"];

export function AccessPanel() {
  const toast = useToast();
  const [st, setSt] = useState<components["schemas"]["AccessSettingsView"] | null>(null);
  const [confirming, setConfirming] = useState<boolean | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    unwrap(api.GET("/api/access/settings")).then(setSt).catch((e: Error) => setError(e.message));
  }, []);
  const apply = async () => {
    if (confirming === null) return;
    try {
      setSt(await unwrap(api.PUT("/api/access/settings", { body: { enabled: confirming } })));
      toast(confirming ? t("Accès privé activé : ce navigateur est autorisé") : t("Accès privé désactivé"), confirming ? "ok" : "warn");
    } catch (e) {
      setError((e as Error).message);
    }
    setConfirming(null);
  };

  if (!st) return <Panel title={t("Accès privé")}><p className="p-4 text-muted">{error ?? t("Chargement…")}</p></Panel>;
  return (
    <Panel title={t("Accès privé")}>
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-wrap gap-2">
          {st.enabled ? <Pill tone="ok">{t("Activé")}</Pill> : <Pill tone="muted">{t("Désactivé")}</Pill>}
          {st.enabled && (st.this_browser ? <Pill tone="ok">{t("Ce navigateur est autorisé")}</Pill>
            : <Pill tone="warn">{t("Ce navigateur n'est pas autorisé pour votre compte")}</Pill>)}
        </div>
        <p className="text-[13px] text-muted">
          {rich("Activé, seuls les navigateurs autorisés par un <b>lien d'accès personnel</b> voient l'écran de connexion. Les autres ne voient qu'une page « Service privé » (ou le formulaire de demande, si les inscriptions sont ouvertes). Chaque compte a ses propres liens : page Comptes → « Accès ». Un client invité reçoit le sien avec son invitation. Supprimer ou désactiver un compte annule ses liens et ses navigateurs.", { b: (s) => <b>{s}</b> })}
        </p>
        <p className="text-[13px] text-muted">
          {rich("Avant d'activer : préparez le lien des autres membres de l'équipe. Votre navigateur, lui, est autorisé d'office. Secours sur le serveur : <cmd>python -m app access-link</cmd>.", { cmd: (s) => <span className="font-mono text-ink">{s} &lt;{t("nom")}&gt;</span> })}
        </p>
        <FormError message={error} />
        <div>
          <Button variant={st.enabled ? "secondary" : "primary"} onClick={() => setConfirming(!st.enabled)}>
            {st.enabled ? t("Désactiver l'accès privé") : t("Activer l'accès privé")}
          </Button>
        </div>
      </div>
      <ConfirmDialog open={confirming !== null} title={confirming ? t("Activer l'accès privé ?") : t("Désactiver l'accès privé ?")}
        confirmLabel={confirming ? t("Activer") : t("Désactiver")} onCancel={() => setConfirming(null)} onConfirm={apply}>
        {confirming
          ? t("Les comptes dont le navigateur n'est pas encore autorisé ne verront plus la connexion : il leur faudra leur lien d'accès.")
          : t("L'écran de connexion redevient visible pour tous ceux qui ont l'adresse de la gateway.")}
      </ConfirmDialog>
    </Panel>
  );
}

/** Fenêtre « Accès » d'un compte : nouveau lien personnel, navigateurs autorisés (à retirer un par un). */
export function AccessModal({ user, onClose }: { user: UserView; onClose: () => void }) {
  const toast = useToast();
  const [devices, setDevices] = useState<Device[] | null>(null);
  const [link, setLink] = useState<{ link: string; expires: number } | null>(null);
  const [removing, setRemoving] = useState<Device | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    unwrap(api.GET("/api/users/{user_id}/devices", { params: { path: { user_id: user.id } } }))
      .then(setDevices).catch((e: Error) => setError(e.message));
  }, [user.id]);
  useEffect(load, [load]);

  const create = async () => {
    setError(null);
    try {
      setLink(await unwrap(api.POST("/api/users/{user_id}/access-link", { params: { path: { user_id: user.id } } })));
    } catch (e) {
      setError((e as Error).message);
    }
  };
  const remove = async () => {
    if (!removing) return;
    try {
      await unwrap(api.DELETE("/api/users/{user_id}/devices/{device_id}", {
        params: { path: { user_id: user.id, device_id: removing.id } },
      }));
      toast(t("Navigateur retiré"), "warn");
      load();
    } catch (e) {
      toast((e as Error).message, "err");
    }
    setRemoving(null);
  };

  return (
    <Modal open wide title={t("Accès de « {name} »", { name: user.username })} onClose={onClose}
      footer={<Button variant="primary" onClick={onClose}>{t("Fermer")}</Button>}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <p className="text-[13px] text-muted">
            {t("Lien personnel, valable 7 jours et une seule fois : à transmettre à {name} seulement. Il autorise le navigateur où il est ouvert. Un nouveau lien remplace le précédent s'il n'a pas servi.", { name: user.username })}
          </p>
          {link ? (
            <>
              <CopyField value={link.link} />
              <span className="text-[12px] text-faint">{t("valable jusqu'au {date}", { date: formatTime(link.expires) })}</span>
            </>
          ) : <div><Button onClick={create} disabled={user.disabled}>{t("Créer un lien d'accès")}</Button></div>}
          <FormError message={error} />
        </div>
        <div>
          <div className="mb-2 font-cond text-[13px] font-semibold uppercase tracking-wider text-faint">{t("Navigateurs autorisés")}</div>
          {devices === null ? <p className="text-muted">{t("Chargement…")}</p> : devices.length === 0 ? (
            <p className="text-[13px] text-muted">{t("Aucun pour l'instant.")}</p>
          ) : (
            <ul className="divide-y divide-line rounded-md border border-line">
              {devices.map((d) => (
                <li key={d.id} className="flex items-center justify-between gap-3 px-3 py-2">
                  <div className="min-w-0">
                    <div className="truncate text-[13px]">{d.user_agent || t("navigateur inconnu")}</div>
                    <div className="text-[12px] text-faint">{t("autorisé {created} · vu {seen}", { created: formatTime(d.created), seen: formatTime(d.last_seen) })}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setRemoving(d)}>{t("Retirer")}</Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      <ConfirmDialog open={removing !== null} title={t("Retirer ce navigateur ?")} confirmLabel={t("Retirer")}
        onCancel={() => setRemoving(null)} onConfirm={remove}>
        {t("Il ne verra plus la connexion (accès privé) tant qu'un nouveau lien n'y est pas ouvert.")}
      </ConfirmDialog>
    </Modal>
  );
}
